import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export const GRADES = [
  "Probationer",
  "Neophyte",
  "Zelator",
  "Theoricus",
  "Practicus",
  "Philosophus",
];

export const GRADE_INDEX = Object.fromEntries(GRADES.map((g, i) => [g, i]));

export function gradeAtLeast(user, minGrade) {
  if (!user || user === false) return false;
  if (user.role === "admin") return true;
  const u = GRADE_INDEX[user.grade] ?? 0;
  const m = GRADE_INDEX[minGrade] ?? 0;
  return u >= m;
}

export const ROMAN = ["0=0", "1=10", "2=9", "3=8", "4=7", "5=6"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = user
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("magick_token");
    if (!token) {
      setUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.mfa_required) {
        // Do NOT persist a session yet — caller must complete /auth/login/2fa.
        return { ok: true, mfa_required: true, mfa_token: data.mfa_token };
      }
      localStorage.setItem("magick_token", data.token);
      setUser(data.user);
      return { ok: true, mfa_required: false };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const loginMfa = async (mfa_token, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login/2fa", { mfa_token, code });
      localStorage.setItem("magick_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const register = async (name, email, password, consent) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", {
        name,
        email,
        password,
        accepted_privacy: !!consent?.privacy,
        accepted_terms: !!consent?.terms,
      });
      localStorage.setItem("magick_token", data.token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network/server error during logout is non-fatal — we still clear the local session below.
      console.warn("Logout request failed; clearing local session anyway.", err?.message || err);
    }
    localStorage.removeItem("magick_token");
    setUser(false);
  };

  const refresh = async () => fetchMe();

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    if (data?.token) localStorage.setItem("magick_token", data.token);
    return true;
  };

  const value = useMemo(
    () => ({ user, error, setError, login, loginMfa, register, logout, refresh, changePassword }),
    // login/register/logout/refresh/changePassword are defined inline above so they change every render;
    // include user+error so consumers re-render when those actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, error]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
